/**
 * Трасса проверки ответа — пересчёт правил валидатора по принятому конверту.
 *
 * Здесь важно, ЧЕЙ это счёт, и сказано это прямо и на экране. Конверт `answer`
 * не несёт списка проверок и нести не будет: принятый ответ по контракту молчит
 * о том, что его проверяли, а имя правила называет только ОТКЛОНЁННЫЙ конверт
 * (`rejected.rule`). Поэтому трасса не приходит с сервера и не притворяется
 * этим — она пересчитана клиентом над тем самым конвертом, который человек
 * видит перед собой.
 *
 * Зачем она тогда нужна. Ответ уже прошёл проверку на сервере: раз он приехал
 * конвертом `answer`, там сошлись все правила. Если то же правило не сходится
 * здесь, разошлись не данные, а ОТВЕТ И ЕГО ПРОВЕРКА по дороге — сборка
 * раскладки после валидатора, сериализация, рассинхрон версий правил и клиента.
 * Расхождение такого рода иначе не видно никому: сервер уверен, что проверил, а
 * человек читает уже неверное.
 *
 * Правила, опирающиеся на реестр (подписи, знаменатели уровня, потолок класса,
 * запрет ранжирования), пересчитать здесь нечем — они названы и помечены
 * «проверено на сервере». Сказать «сошлось» о том, чего не проверял, хуже, чем
 * честно назвать границу собственного знания.
 *
 * Обозначения правил («1», «4б», «10б») — те же, что в дизайне, в валидаторе и
 * в журнале. Переводить их нельзя: строка журнала и строка экрана должны
 * читаться как одно правило без словаря переводов.
 */

import type { AiAnswerOk, AiBlock } from "../../api/client";
import { defineMessages, type Message } from "../../i18n/define";
import { tr } from "../../i18n/runtime";
import { knownRoute } from "./answerModel";

/** Строки трассы: «что требует правило» и «чем разошлось». Номера правил не переводятся. */
const m = defineMessages({
  r1: {
    id: "answer.trace.rule1.what",
    defaultMessage: "вердикт ровно один; плашка демонстрации — ровно при синтетике",
    description: "Правило 1 в списке проверок ответа ИИ: что оно требует (строка списка, строчными)",
  },
  r1Verdicts: {
    id: "answer.trace.rule1.drift.verdicts",
    defaultMessage: "блоков вердикта: {count}",
    description: "Правило 1, расхождение: число блоков вердикта в ответе; {count} — число",
  },
  r1NoNotice: {
    id: "answer.trace.rule1.drift.noNotice",
    defaultMessage: "синтетика в срезе есть, а плашки демонстрации нет",
    description: "Правило 1, расхождение: в данных есть синтетика, но нет плашки «демонстрация»",
  },
  r1ExtraNotice: {
    id: "answer.trace.rule1.drift.extraNotice",
    defaultMessage: "плашка демонстрации есть, а синтетики в срезе нет",
    description: "Правило 1, расхождение: плашка «демонстрация» показана, хотя синтетических данных нет",
  },
  fieldDenominator: {
    id: "answer.trace.rule3.field.denominator",
    defaultMessage: "знаменатель",
    description: "Название поля таблицы в сообщении правила 3 («нет поля «знаменатель»»); строчными",
  },
  fieldEmpty: {
    id: "answer.trace.rule3.field.empty",
    defaultMessage: "слова пустоты",
    description: "Название поля таблицы в сообщении правила 3: слова, которые печатаются при пустой таблице; строчными",
  },
  fieldOrder: {
    id: "answer.trace.rule3.field.order",
    defaultMessage: "порядок",
    description: "Название поля таблицы в сообщении правила 3: по какому правилу упорядочены строки; строчными",
  },
  r3MissingField: {
    id: "answer.trace.rule3.missingField",
    defaultMessage: "{block}: нет поля «{field}»",
    description: "Правило 3, расхождение: {block} — служебное имя блока-таблицы, {field} — название поля",
  },
  r3: {
    id: "answer.trace.rule3.what",
    defaultMessage: "у таблицы объявлены знаменатель, порядок и слова пустоты",
    description: "Правило 3 в списке проверок ответа ИИ: что оно требует (строчными)",
  },
  r3Drift: {
    id: "answer.trace.rule3.drift",
    defaultMessage: "таблиц без объявления: {list}",
    description: "Правило 3, расхождение; {list} — перечень таблиц и недостающих полей через точку с запятой",
  },
  r7: {
    id: "answer.trace.rule7.what",
    defaultMessage: "«без данных» больше нуля ⇔ в ответе есть блок пробела",
    description: "Правило 7 в списке проверок ответа ИИ: что оно требует; ⇔ означает «тогда и только тогда»",
  },
  r7NoGap: {
    id: "answer.trace.rule7.drift.noGap",
    defaultMessage: "без данных {count}, а блока пробела нет",
    description: "Правило 7, расхождение; {count} — число объектов без данных",
  },
  r7ExtraGap: {
    id: "answer.trace.rule7.drift.extraGap",
    defaultMessage: "блок пробела есть, а «без данных» — ноль",
    description: "Правило 7, расхождение: блок «нет данных» показан, хотя таких объектов нет",
  },
  r7b: {
    id: "answer.trace.rule7b.what",
    defaultMessage: "«с данными» + «без данных» не больше популяции",
    description: "Правило 7б в списке проверок ответа ИИ: что оно требует; популяция — все объекты, о которых спрашивали",
  },
  r7bDrift: {
    id: "answer.trace.rule7b.drift",
    defaultMessage: "{observed} + {unknown} больше популяции {eligible}",
    description: "Правило 7б, расхождение; все аргументы — числа",
  },
  r8: {
    id: "answer.trace.rule8.what",
    defaultMessage: "при сценарных входах в ответе нет действий-черновиков",
    description: "Правило 8 в списке проверок ответа ИИ: что оно требует (сценарные входы — допущения «а что если»)",
  },
  r8Drift: {
    id: "answer.trace.rule8.drift",
    defaultMessage: "сценарные входы и действие-черновик в одном ответе",
    description: "Правило 8, расхождение",
  },
  r9: {
    id: "answer.trace.rule9.what",
    defaultMessage: "ряд назван трендом только от трёх точек",
    description: "Правило 9 в списке проверок ответа ИИ: что оно требует",
  },
  r9Drift: {
    id: "answer.trace.rule9.drift",
    defaultMessage: "рядов короче трёх точек: {count}",
    description: "Правило 9, расхождение; {count} — число рядов",
  },
  r10: {
    id: "answer.trace.rule10.what",
    defaultMessage: "блок с величинами назвал метод словами",
    description: "Правило 10 в списке проверок ответа ИИ: что оно требует",
  },
  r10Drift: {
    id: "answer.trace.rule10.drift",
    defaultMessage: "блоков без метода: {count}",
    description: "Правило 10, расхождение; {count} — число блоков",
  },
  r10b: {
    id: "answer.trace.rule10b.what",
    defaultMessage: "дата ответа равна дате принятого среза",
    description: "Правило 10б в списке проверок ответа ИИ: что оно требует (срез — снимок данных на дату)",
  },
  r10bDrift: {
    id: "answer.trace.rule10b.drift",
    defaultMessage: "ответ на {answerDate}, срез на {snapshotDate}",
    description: "Правило 10б, расхождение; аргументы — две даты в виде ГГГГ-ММ-ДД",
  },
  r11: {
    id: "answer.trace.rule11.what",
    defaultMessage: "переход ведёт на экран, который у клиента есть",
    description: "Правило 11 в списке проверок ответа ИИ: что оно требует («клиент» — само приложение)",
  },
  r11Drift: {
    id: "answer.trace.rule11.drift",
    defaultMessage: "адресов без экрана: {list}",
    description: "Правило 11, расхождение; {list} — адреса переходов через запятую",
  },
  r13: {
    id: "answer.trace.rule13.what",
    defaultMessage: "у каждой причины названо основание",
    description: "Правило 13 в списке проверок ответа ИИ: что оно требует",
  },
  r13Drift: {
    id: "answer.trace.rule13.drift",
    defaultMessage: "блоков причин без основания: {count}",
    description: "Правило 13, расхождение; {count} — число блоков",
  },
  s2: {
    id: "answer.trace.rule2.what",
    defaultMessage: "первый взгляд уложен в три бюджета, излишек перенесён в раскрытие",
    description: "Правило 2 (проверяется на сервере): что оно требует; «первый взгляд» — то, что видно до раскрытия",
  },
  s4: {
    id: "answer.trace.rule4.what",
    defaultMessage: "все фразы — статьи словаря, слоты связаны так, как объявлены",
    description: "Правило 4 (проверяется на сервере): что оно требует",
  },
  s4b: {
    id: "answer.trace.rule4b.what",
    defaultMessage: "каждая подпись взята из labels реестра",
    description: "Правило 4б (проверяется на сервере): что оно требует; labels — служебное имя справочника подписей, не переводить",
  },
  s5: {
    id: "answer.trace.rule5.what",
    defaultMessage: "знаменатель величины принадлежит уровню ответа",
    description: "Правило 5 (проверяется на сервере): что оно требует; знаменатель — «из скольки» считается доля",
  },
  s5b: {
    id: "answer.trace.rule5b.what",
    defaultMessage: "сравнение не расширяет рамки остальных блоков",
    description: "Правило 5б (проверяется на сервере): что оно требует",
  },
  s6: {
    id: "answer.trace.rule6.what",
    defaultMessage: "структурный запрет оценки людей",
    description: "Правило 6 (проверяется на сервере): ответ не оценивает и не ранжирует людей",
  },
  s12: {
    id: "answer.trace.rule12.what",
    defaultMessage: "класс блока не выше потолка инструмента и не выше худшего входа",
    description: "Правило 12 (проверяется на сервере): класс достоверности блока не выше допустимого",
  },
});

/**
 * Что стало с правилом на ЭТОМ конверте.
 *
 * `held` — правило пересчитано здесь и сошлось; `drift` — пересчитано и не
 * сошлось; `server` — пересчитать нечем, правило смотрит в реестр или словарь.
 */
export type TraceOutcome = "held" | "drift" | "server";

export interface TraceRow {
  /** Обозначение правила — то же, что в дизайне и в журнале (`RuleId`). */
  rule: string;
  /** Что правило требует — одной строкой. */
  what: string;
  outcome: TraceOutcome;
  /** Чем именно оно разошлось. Есть только у `drift`. */
  drift?: string;
}

/** Правила, которые смотрят в реестр и словарь: клиенту их не пересчитать. */
const SERVER_ONLY: readonly (readonly [string, Message])[] = [
  ["2", m.s2],
  ["4", m.s4],
  ["4б", m.s4b], // i18n-ignore: обозначение правила
  ["5", m.s5],
  ["5б", m.s5b], // i18n-ignore: обозначение правила
  ["6", m.s6],
  ["12", m.s12],
];

export function validatorTrace(envelope: AiAnswerOk): TraceRow[] {
  const spec = envelope.spec;
  const all: AiBlock[] = [...(spec.blocks ?? []), ...(spec.disclosure ?? [])];
  const coverage = spec.coverage;
  const basis = envelope.basis;
  const rows: TraceRow[] = [];

  const check = (rule: string, what: string, ok: boolean, drift: string) => {
    rows.push(
      ok
        ? { rule, what, outcome: "held" }
        : { rule, what, outcome: "drift", drift },
    );
  };

  /* 1 — порядок: вердикт ровно один, плашка демо ровно при синтетике. */
  const verdicts = all.filter((block) => block.type === "verdict").length;
  const notices = all.filter((block) => block.type === "demo_notice").length;
  const synthetic = (basis.classes_present ?? []).includes("synthetic_demo");
  check(
    "1",
    tr(m.r1),
    verdicts === 1 && (synthetic ? notices > 0 : notices === 0),
    verdicts !== 1
      ? tr(m.r1Verdicts, { count: verdicts })
      : synthetic
        ? tr(m.r1NoNotice)
        : tr(m.r1ExtraNotice),
  );

  /* 3 — таблица объявляет знаменатель, порядок и слова пустоты.
   *
   * Все три поля требуются БЕЗУСЛОВНО, как у сервера (`_rule_3_required_fields`):
   * поле `empty` обязано быть и у таблицы СО строками. Требовать его только у
   * пустой таблицы значило бы проверять слабее проверяемого — и молчать там,
   * где сервер ответ отклонил бы. Смысл трассы ровно в том, чтобы расхождение
   * было видно. */
  const missingFields: string[] = [];
  for (const block of all) {
    if (block.type !== "table") continue;
    for (const [name, value] of [
      [tr(m.fieldDenominator), block.denominator],
      [tr(m.fieldEmpty), block.empty],
      [tr(m.fieldOrder), block.order],
    ] as const) {
      if (value === undefined || value === null) {
        missingFields.push(tr(m.r3MissingField, { block: block.id, field: name }));
      }
    }
  }
  check(
    "3",
    tr(m.r3),
    missingFields.length === 0,
    tr(m.r3Drift, { list: missingFields.join("; ") }),
  );

  /* 7 — отсутствие данных названо блоком, а не пустотой. */
  const hasGap = all.some((block) => block.type === "gap");
  check(
    "7",
    tr(m.r7),
    coverage.unknown > 0 === hasGap,
    coverage.unknown > 0
      ? tr(m.r7NoGap, { count: coverage.unknown })
      : tr(m.r7ExtraGap),
  );

  /* 7б — арифметика покрытия. */
  check(
    "7б", // i18n-ignore: обозначение правила
    tr(m.r7b),
    coverage.observed + coverage.unknown <= coverage.eligible,
    tr(m.r7bDrift, {
      observed: coverage.observed,
      unknown: coverage.unknown,
      eligible: coverage.eligible,
    }),
  );

  /* 8 — сценарные входы запрещают действия-черновики. */
  const drafts = all.some(
    (block) =>
      block.type === "actions" &&
      (block.items ?? []).some((item) => item.kind === "draft"),
  );
  check(
    "8",
    tr(m.r8),
    !basis.scenario_inputs || !drafts,
    tr(m.r8Drift),
  );

  /* 9 — рядом называют только три точки и больше. */
  const shortTrends = all.filter(
    (block) => block.type === "trend" && (block.points ?? []).length < 3,
  ).length;
  check(
    "9",
    tr(m.r9),
    shortTrends === 0,
    tr(m.r9Drift, { count: shortTrends }),
  );

  /* 10 — блок с величинами называет метод словами. */
  const mute = all.filter((block) => {
    if (!("provenance" in block)) return false;
    const provenance = block.provenance;
    if (!provenance) return true;
    return !(provenance.method ?? "").trim();
  }).length;
  check(
    "10",
    tr(m.r10),
    mute === 0,
    tr(m.r10Drift, { count: mute }),
  );

  /* 10б — дата ответа равна дате принятого среза. */
  const snapshotDate = basis.snapshot.as_of ?? null;
  check(
    "10б", // i18n-ignore: обозначение правила
    tr(m.r10b),
    snapshotDate === null || snapshotDate === basis.as_of,
    tr(m.r10bDrift, { answerDate: String(basis.as_of), snapshotDate: String(snapshotDate) }),
  );

  /* 11 — переходы ведут только на известные клиенту адреса. */
  const routes: string[] = [];
  for (const block of all) {
    if (block.type === "actions") {
      routes.push(...(block.items ?? []).map((item) => item.to));
    }
    if (block.type === "table") {
      for (const row of block.rows ?? []) {
        if (row.drill_down) routes.push(row.drill_down);
      }
    }
    if (block.type === "kpi_row") {
      for (const item of block.items ?? []) {
        if (item.drill_down) routes.push(item.drill_down);
      }
    }
    if (block.type === "deviations") {
      for (const item of block.items ?? []) {
        if (item.drill_down) routes.push(item.drill_down);
      }
    }
  }
  const dead = routes.filter((route) => !knownRoute(route));
  check(
    "11",
    tr(m.r11),
    dead.length === 0,
    tr(m.r11Drift, { list: dead.join(", ") }),
  );

  /* 13 — причина без основания не печатается. */
  const baseless = all.filter(
    (block) =>
      block.type === "causes" &&
      (block.items ?? []).some((item) => !item.basis),
  ).length;
  check(
    "13",
    tr(m.r13),
    baseless === 0,
    tr(m.r13Drift, { count: baseless }),
  );

  for (const [rule, what] of SERVER_ONLY) {
    rows.push({ rule, what: tr(what), outcome: "server" });
  }

  return rows;
}

/** Сколько правил разошлось. Ноль — трасса сошлась целиком. */
export function traceDrift(rows: readonly TraceRow[]): number {
  return rows.filter((row) => row.outcome === "drift").length;
}

/**
 * Сколько правил пересчитано ЗДЕСЬ — то есть о скольких клиент вправе
 * что-либо утверждать.
 *
 * Длина трассы для этого не годится: в ней лежат и строки `server`, которые
 * клиент не считал и честно пометил «проверено на сервере». Назвать их своим
 * счётом значило бы приписать себе ровно те проверки, границу которых трасса
 * и заведена очерчивать, — и сделать это в подписи, которую человек читает
 * ДО того, как раскроет список.
 */
export function traceRecounted(rows: readonly TraceRow[]): number {
  return rows.filter((row) => row.outcome !== "server").length;
}
